import { useMemo, useState } from "react";
import { useApp } from "@/store/AppStore";
import { useAsesor } from "@/hooks/useAsesor";
import SectionTitle from "@/components/base/SectionTitle";
import AdvisorEncargoList from "@/pages/asesor/encargo/components/AdvisorEncargoList";
import { inputClass, labelClass, primaryButtonClass } from "@/components/base/fieldStyles";
import { formatCOP } from "@/utils/format";
import type { DeliveryMode } from "@/types";

// Pedido por Encargo: el asesor elige CUALQUIER producto del catálogo completo
// (no solo lo que tiene en consignación), ve el precio público, su costo con el
// descuento configurado y su ganancia estimada, y dispara la alerta al admin.
const EncargoPage = () => {
  const { data, pushNotification, addEncargo } = useApp();
  const { name, advisorId, margin } = useAsesor();

  const [query, setQuery] = useState("");
  const [productId, setProductId] = useState("");
  const [qty, setQty] = useState(1);
  const [note, setNote] = useState("");
  const [delivery, setDelivery] = useState<DeliveryMode>("recojo");
  const [error, setError] = useState("");
  const [feedback, setFeedback] = useState("");

  const fee = delivery === "domicilio" ? data.settings.logisticsFee : 0;

  // Desplegable con TODO el catálogo (ordenado por nombre). El buscador filtra
  // las opciones para encontrar rápido entre cientos de referencias.
  const options = useMemo(() => {
    const sorted = [...data.products].sort((a, b) => a.name.localeCompare(b.name));
    const q = query.trim().toLowerCase();
    if (!q) return sorted;
    return sorted.filter(
      (p) => p.name.toLowerCase().includes(q) || p.sku.toLowerCase().includes(q)
    );
  }, [data.products, query]);

  const selected = data.products.find((p) => p.id === productId) ?? null;
  const unitCost = selected ? Math.round(selected.pricePublic * (1 - margin / 100)) : 0;
  const unitProfit = selected ? selected.pricePublic - unitCost : 0;
  const qtyNum = Number(qty) || 0;
  const publicTotal = (selected?.pricePublic ?? 0) * qtyNum + fee;
  const costTotal = unitCost * qtyNum + fee;
  const profitTotal = unitProfit * qtyNum;

  const submit = async () => {
    setError("");
    if (!selected) {
      setError("Selecciona el producto que deseas encargar.");
      return;
    }
    if (qtyNum <= 0) {
      setError("La cantidad debe ser mayor a cero.");
      return;
    }

    await addEncargo({
      id: "",
      advisorId,
      advisorName: name,
      productId: selected.id,
      productName: selected.name,
      qty: qtyNum,
      unitPrice: selected.pricePublic,
      note: note.trim(),
      delivery,
      logisticsFee: fee,
      status: "pendiente",
      date: new Date().toISOString(),
    });

    await pushNotification({
      audience: "admin",
      title: "Nuevo pedido por encargo",
      message: `${name} solicitó ${qtyNum} x ${selected.name} · ${
        delivery === "domicilio" ? "Domicilio" : "Recojo"
      }${note.trim() ? ` · ${note.trim()}` : ""}`,
      kind: "order",
      link: "/admin/encargos",
    });

    setFeedback(`Pedido enviado: ${qtyNum} x ${selected.name}. Juanka lo verá en su panel de pedidos.`);
    setProductId("");
    setQty(1);
    setNote("");
    setQuery("");
    setTimeout(() => setFeedback(""), 3000);
  };

  return (
    <div className="pb-24">
      <SectionTitle
        text="Pedido por Encargo"
        subtitle="¿No tienes el producto en consignación? Pídelo del catálogo completo y Juanka lo repone. Verás tu costo y tu ganancia con tu descuento."
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <div className="glass-panel rounded-lg p-4">
            <div className="relative mb-3">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-accent-300">
                <i className="ri-search-line" />
              </span>
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Filtra el catálogo Amway para encontrar el producto..."
                className="w-full bg-background-100/70 border border-background-300/50 rounded-md pl-9 pr-3 py-2 text-sm text-foreground-950 placeholder:text-foreground-500 outline-none focus:border-accent-500/50 focus:ring-2 focus:ring-accent-500/25"
                aria-label="Buscar producto para encargo"
              />
            </div>

            <label className={labelClass}>Producto del catálogo</label>
            <div className="relative">
              <select
                value={productId}
                onChange={(e) => setProductId(e.target.value)}
                className={`${inputClass} appearance-none pr-9 cursor-pointer`}
                aria-label="Seleccionar producto del catálogo"
              >
                <option value="">— Selecciona un producto —</option>
                {options.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} · {p.sku}
                  </option>
                ))}
              </select>
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-foreground-500">
                <i className="ri-arrow-down-s-line" />
              </span>
            </div>
            {options.length === 0 && (
              <p className="text-[11px] text-foreground-500 mt-2">
                Sin resultados para tu búsqueda.
              </p>
            )}

            {selected && (
              <div className="mt-4 rounded-lg border border-background-300/40 bg-background-200/30 p-3 animate-fade-up">
                <div className="flex items-center gap-2 mb-3">
                  <p className="text-sm font-semibold text-foreground-950 truncate">
                    {selected.name}
                  </p>
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-secondary-500/20 text-secondary-100 whitespace-nowrap">
                    {selected.category}
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <div className="rounded-md bg-background-100/60 border border-background-300/40 px-3 py-2">
                    <p className="text-[10px] text-foreground-500">Precio público</p>
                    <p className="text-sm font-bold text-foreground-950">
                      {formatCOP(selected.pricePublic)}
                    </p>
                  </div>
                  <div className="rounded-md bg-background-100/60 border border-background-300/40 px-3 py-2">
                    <p className="text-[10px] text-foreground-500">
                      Tu costo · dto. {margin}%
                    </p>
                    <p className="text-sm font-bold text-foreground-950">{formatCOP(unitCost)}</p>
                  </div>
                  <div className="rounded-md bg-accent-500/10 border border-accent-500/30 px-3 py-2">
                    <p className="text-[10px] text-accent-300">Tu ganancia / unidad</p>
                    <p className="text-sm font-bold text-accent-200">{formatCOP(unitProfit)}</p>
                  </div>
                </div>
                <p className="text-[10px] text-foreground-500 mt-2 flex items-center gap-1.5">
                  <i className="ri-information-line" /> Tu costo es el precio público menos el{" "}
                  {margin}% de descuento que te asignó Juanka al registrarte.
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="glass-panel-strong rounded-lg p-5 h-fit">
          <div className="flex items-center gap-2 mb-4">
            <span className="w-8 h-8 rounded-md bg-primary-500/25 text-accent-200 flex items-center justify-center">
              <i className="ri-shopping-cart-2-line" />
            </span>
            <h3 className="editable-title text-base font-bold">Solicitar encargo</h3>
          </div>

          <div className="space-y-3">
            <div>
              <label className={labelClass}>Producto</label>
              <p className="text-sm text-foreground-950 font-semibold">
                {selected ? selected.name : "— selecciona uno —"}
              </p>
            </div>
            <div>
              <label className={labelClass}>Cantidad</label>
              <input
                type="number"
                min={1}
                value={qty}
                onChange={(e) => setQty(Number(e.target.value) || 0)}
                className={inputClass}
              />
            </div>

            <div>
              <label className={labelClass}>Logística de entrega</label>
              <div className="grid grid-cols-2 gap-2">
                {(
                  [
                    { key: "recojo" as DeliveryMode, label: "Recojo en tienda", icon: "ri-store-2-line" },
                    { key: "domicilio" as DeliveryMode, label: "Domicilio", icon: "ri-truck-line" },
                  ]
                ).map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => setDelivery(opt.key)}
                    className={`rounded-md border px-2 py-2 text-[11px] font-semibold cursor-pointer flex flex-col items-center gap-1 ${
                      delivery === opt.key
                        ? "border-accent-500/60 bg-accent-500/10 text-accent-200"
                        : "border-background-300/40 text-foreground-600 hover:border-accent-500/30"
                    }`}
                  >
                    <i className={`${opt.icon} text-base`} />
                    {opt.label}
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-foreground-500 mt-1.5">
                {delivery === "domicilio"
                  ? `Se aplica fee logístico de ${formatCOP(fee)}.`
                  : "Recojo en tienda/oficina, sin fee logístico."}
              </p>
            </div>

            <div>
              <label className={labelClass}>Nota para Juanka (opcional)</label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                maxLength={500}
                rows={3}
                placeholder="Ej: lo necesito para el sábado, cliente frecuente..."
                className={`${inputClass} resize-none`}
              />
            </div>

            {selected && qtyNum > 0 && (
              <div className="space-y-1.5">
                <div className="rounded-md bg-background-200/40 border border-background-300/40 px-3 py-2 flex items-center justify-between">
                  <span className="text-[11px] text-foreground-500">Total público estimado</span>
                  <span className="text-sm font-bold text-foreground-950">
                    {formatCOP(publicTotal)}
                  </span>
                </div>
                <div className="rounded-md bg-background-200/40 border border-background-300/40 px-3 py-2 flex items-center justify-between">
                  <span className="text-[11px] text-foreground-500">Pagas a Juanka</span>
                  <span className="text-sm font-bold text-foreground-950">
                    {formatCOP(costTotal)}
                  </span>
                </div>
                <div className="rounded-md bg-accent-500/10 border border-accent-500/30 px-3 py-2 flex items-center justify-between">
                  <span className="text-[11px] text-accent-300">Tu ganancia estimada</span>
                  <span className="text-sm font-bold text-accent-200">
                    {formatCOP(profitTotal)}
                  </span>
                </div>
              </div>
            )}

            {feedback && (
              <p className="text-[11px] text-accent-200 flex items-center gap-1.5">
                <i className="ri-checkbox-circle-line" /> {feedback}
              </p>
            )}
            {error && (
              <p className="text-[11px] text-primary-200 flex items-center gap-1.5">
                <i className="ri-error-warning-line" /> {error}
              </p>
            )}

            <button onClick={submit} className={`${primaryButtonClass} w-full`}>
              <i className="ri-send-plane-line" /> Enviar pedido
            </button>
            <p className="text-[10px] text-foreground-500 text-center">
              Se enviará como pedido al panel del administrador.
            </p>
          </div>
        </div>
      </div>

      <div className="mt-6">
        <SectionTitle
          text="Mis pedidos por encargo"
          subtitle="Estado y desglose de los pedidos que le hiciste a Juanka: compra o consignación, lo que te costó y tu fee logístico."
        />
        <AdvisorEncargoList advisorId={advisorId} />
      </div>
    </div>
  );
};

export default EncargoPage;